import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List, fromJS } from 'immutable'
import compose from 'recompose/compose'
import _get from 'lodash/get'
import _map from 'lodash/map'
import _parseInt from 'lodash/parseInt'
import _replace from 'lodash/replace'
import _size from 'lodash/size'
import _split from 'lodash/split'
import _toString from 'lodash/toString'
import _includes from 'lodash/includes'
import { connect } from 'react-redux'
import { useLazyQuery } from '@apollo/client'
import { get as getConfig } from '../../config/index'
import { getBoundActions } from '../../actions/index'
import {
  getSeo,
  isFullUrl,
} from '../../services/url/index'
import { historyRedirect } from '../../services/navigation/index'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { LTM } from '../../services/currency/index'
import { JP, KR, IN } from '../../services/country/index'
import Sherpa, { TYPE_LARGE } from '../Sherpa/index'
import {
  PLAN_SKU_ANNUAL_SPECIAL,
  replaceSkuToPriceIncreasedSku,
  skuIsGift,
} from '../../services/plans/index'
import { connect as connectPage, PAGE_SEO_TYPE_MANUAL } from '../Page/connect'
import {
  URL_ACCOUNT,
  URL_ACCOUNT_CREATION,
  URL_CART_ACCOUNT_CREATION_CREATE,
  URL_HOME,
  URL_UNAVAILABLE,
  URL_GIFT,
  URL_PLAYLIST_ANONYMOUS,
  URL_ACCOUNT_CHANGE_PLAN,
} from '../../services/url/constants'
import {
  APPROVE_REMOTE_LOGIN_CODE_PROCESSING,
} from '../../services/remote-login-code/index'
import { getPrimary } from '../../services/languages/index'
import {
  cleanSku,
  userCheckoutAccountChangePlanAllowed,
} from '../../services/checkout/index'
import {
  formatBillingPlans,
} from '../../services/plans/utils'
import { GET_PLANS_BY_SKU } from '../../services/plans/query'

const config = getConfig()
const giftEnabled = !!_get(config, ['features', 'gift', 'checkout'])
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

const HOSTNAME = _get(global, ['location', 'hostname'], 'www.gaia.com')
const HOSTNAME_REGEX = new RegExp(`^https?://${HOSTNAME}/?`, 'i')

function handleRedirect (props, url) {
  const { history, auth, user = Map() } = props
  const language = user.getIn(['data', 'language'])
  if (isFullUrl(url)) {
    if (HOSTNAME_REGEX.test(url)) {
      window.location.href = url
      return
    }
    historyRedirect({
      url: '/',
      language,
      history,
      auth,
    })
    return
  }
  historyRedirect({
    language,
    history,
    auth,
    url,
  })
}

function handleAddToPlaylist (props, addToPlaylist, url) {
  const { auth, location, batchPlaylistItems } = props
  const showLogin = _get(location, ['query', 'login'])
  if (!getAuthIsLoggedIn(auth)) {
    if (!window) {
      return
    }
    if (showLogin === undefined) {
      window.location.href = `${window.location.href}&login=true`
    }
    return
  }

  handleRedirect(props, `${url || '/'}`)

  const ids = _map(_split(addToPlaylist, ','), _parseInt)

  if (_size(ids) === 0) {
    return
  }

  batchPlaylistItems({ ids, type: 'default', add: true })
}

const handlePlaylistCreate = (props) => {
  const {
    auth,
    location,
    createCustomPlaylistFromDeeplink,
    language,
    history,
  } = props

  const query = _get(location, ['query'])
  const { contentIds, playlistCreate } = query

  if (!getAuthIsLoggedIn(auth)) {
    historyRedirect({
      url: URL_PLAYLIST_ANONYMOUS,
      language,
      history,
      auth,
      query,
    })
    return // return here to prevent action from being called on every update
  }
  // https://help.branch.io/using-branch/docs/deep-linking
  createCustomPlaylistFromDeeplink({ name: playlistCreate, contentIds })
}

async function handlePlansSku (options = {}) {
  const { props, graphQLPlanData, graphQLPlanDataLoading } = options

  if (planGraphQLActive && (!graphQLPlanData || graphQLPlanDataLoading)) {
    return
  }

  let { sku } = options
  const {
    setPlansLocalizedSelection,
    setCheckoutPlanPreSelected,
    userAccount,
    history,
    plans,
    auth,
    setPageSeo,
    location,
    user = Map(),
    setPlansSelection,
  } = props
  const whitespaceRegex = /\s/g
  const cleanedSku = cleanSku(sku)
  const language = user.getIn(['data', 'language'])
  const userCountry = user.getIn(['data', 'requestCountry'])
  const isFallSpecialSku = cleanedSku === cleanSku(PLAN_SKU_ANNUAL_SPECIAL)
  const fallSpecialAllowedCountries = [JP, KR, IN]
  const isGiftSku = skuIsGift(sku)

  if (isGiftSku) {
    const giftPathname = `${URL_GIFT}/${cleanedSku}`
    const seo = getSeo({
      pathname: giftPathname,
      loggedIn: !!auth.get('jwt'),
    })
    historyRedirect({
      url: giftEnabled
        ? giftPathname
        : URL_UNAVAILABLE,
      language,
      history,
      auth,
    })
    setPageSeo({
      title: seo.title,
      description: seo.description,
      noFollow: seo.noFollow,
      noIndex: seo.noIndex,
      location,
    })
    return
  }

  if (
    (!planGraphQLActive
    && !plans.get('processing')
    && !plans.get('processingLocalized')
    && !plans.get('selection')
    && !plans.get('planError'))
    || (planGraphQLActive && graphQLPlanData.size)
  ) {
    const dataPlans = planGraphQLActive ? graphQLPlanData : plans.getIn(['data', 'plans'], List())
    const currencyIso = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'currencyIso'])
    const latamPricing = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'latamPricing'])
    const currency = latamPricing ? LTM : currencyIso
    // Get the translation data for this plan to help localization
    // strip spaces in the sku coming from the plan
    // and compare it to the sku coming in from the url
    const plan = dataPlans.find((item) => (
      _replace(item.get('sku'), whitespaceRegex, '').toLowerCase() === cleanedSku
    ))

    if (!getAuthIsLoggedIn(auth) && dataPlans.size === 0) {
      // wait to load plans - watcher in plans service should load these
      return
    }

    if (getAuthIsLoggedIn(auth)) {
      if (dataPlans.size === 0 || userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()).size === 0) {
        // wait to load plans and userAccount details - watcher in plans service should load these
        return
      }

      // if the user is logged in and comes in with a sku that is in the allowed change plan skus
      // then redirect them to the account change plan area as opposed to the checkout flow,
      // as the checkout flow will deny them
      if (
        userCheckoutAccountChangePlanAllowed({ userAccount })
      ) {
        historyRedirect({
          url: `${URL_ACCOUNT_CHANGE_PLAN}?sku=${sku}`,
          language,
          history,
          auth,
        })

        return
      }
    }

    // If we find a plan use it instead of the sku since it has
    // translation data.
    if (plan) {
      sku = undefined
    }

    let planData = Map()

    if (!planGraphQLActive) {
      planData = await setPlansLocalizedSelection({
        sku, plan, language, currency,
      })

      if (!planData.getIn(['plans', 0, 'sku']) || !planData.getIn(['plans', 0, 'checkoutEnabled'])) {
        historyRedirect({
          url: URL_ACCOUNT_CREATION,
          language,
          history,
          auth,
        })
        return
      }
    }

    if (planGraphQLActive) {
      planData = plan

      // bail early if the user is trying to access the fall special sku
      // from a country that is not allowed
      if (isFallSpecialSku && !_includes(fallSpecialAllowedCountries, userCountry)) {
        historyRedirect({
          url: URL_ACCOUNT_CREATION,
          language,
          history,
          auth,
        })
        return
      }

      if (!Map.isMap(planData) || !planData.size || !planData.get('checkoutEnabled')) {
        historyRedirect({
          url: URL_ACCOUNT_CREATION,
          language,
          history,
          auth,
        })
        return
      }

      setPlansSelection(planData)
    }

    // for the Go sku flow,
    // set a value in state so we know the user came in with a pre-selected plan
    setCheckoutPlanPreSelected(true)

    // send the user to the second step of checkout when coming through the /go sku flow
    historyRedirect({
      url: URL_CART_ACCOUNT_CREATION_CREATE,
      language,
      history,
      auth,
    })
    return
  }

  if (planGraphQLActive) {
    // if we can't figure out what to do, send the user to the first step of checkout
    historyRedirect({
      url: URL_ACCOUNT_CREATION,
      language,
      history,
      auth,
    })
  }
}

async function handleRemoteLogin (options = {}) {
  const { props, remoteLoginCode } = options
  const {
    doConsumeRemoteLoginCode,
    remoteLoginCode: loginCodeState,
    location = {},
    history,
    auth,
    user = Map(),
  } = props
  const language = user.getIn(['data', 'language'])
  const processing = loginCodeState.getIn([APPROVE_REMOTE_LOGIN_CODE_PROCESSING])
  const redirectTo = _get(location, ['query', 'redirectTo'], null) || null

  // The remote login code is empty
  if (!remoteLoginCode) {
    historyRedirect({
      language,
      history,
      auth,
      url: URL_HOME,
    })
    return
  }

  if (!processing) {
    try {
      const { success } = await doConsumeRemoteLoginCode({ remoteLoginCode })
      const url = success ? redirectTo || URL_ACCOUNT : URL_HOME

      // redirect to the url
      handleRedirect(props, url)
    } catch (e) {
      // error, redirect to HP
      historyRedirect({
        language,
        history,
        auth,
        url: URL_HOME,
      })
    }
  }
}

function updateSeo (props) {
  // @TODO replace with connectedPage HOC
  const {
    page, auth, location, setPageSeo,
  } = props
  const path = location.pathname + _toString(location.search || '')
  if (page.get('path') === path) {
    return
  }
  const seo = getSeo({
    pathname: location.pathname,
    loggedIn: !!auth.get('jwt'),
  })
  setPageSeo({
    title: seo.title,
    description: seo.description,
    noFollow: seo.noFollow,
    noIndex: seo.noIndex,
    location,
  })
}

function updateData (props) {
  const {
    location,
    inboundTracking,
    sku,
    graphQLPlanData,
    graphQLPlanDataLoading,
  } = props
  const url = _get(location, ['query', 'url'])
  const addToPlaylist = _get(location, ['query', 'playlistAdd'])
  const createPlaylist = _get(location, ['query', 'playlistCreate'])
  const remoteLoginCode = _get(location, ['query', 'remoteLoginCode'])
  // Wait for inbound tracking to finished processing
  // Check for empty sku
  if (
    !inboundTracking.get('initialized')
    || inboundTracking.get('processing')
    || !inboundTracking.get('data')
  ) {
    return
  }

  // We can now do work
  if (sku) {
    handlePlansSku({
      props,
      sku,
      graphQLPlanData,
      graphQLPlanDataLoading,
    })
  } else if (addToPlaylist) {
    handleAddToPlaylist(props, addToPlaylist, url)
  } else if (url) {
    handleRedirect(props, url)
  } else if (createPlaylist) {
    handlePlaylistCreate(props)
  } else if (remoteLoginCode) {
    handleRemoteLogin({ props, remoteLoginCode })
  } else {
    handleRedirect(props, '/')
  }
}

function GoPage (props) {
  const {
    userAccount,
    user,
    auth,
    location,
  } = props
  const querySku = _get(location, ['query', 'sku'], '')
  const exactSku = replaceSkuToPriceIncreasedSku(querySku)
  const authJwt = auth.get('jwt')
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const userLanguage = getPrimary(user.getIn(['data', 'language']))
  const userBillingSubscriptions = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()) || Map()
  let userAccountDataReady = true
  const [goPageUserLanguage, setGoPageUserLanguage] = useState(userLanguage)
  const [
    getPlansDataBySku,
    { data: planData, loading: planDataLoading },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const formattedPlanData = fromJS(formatBillingPlans(planData))

  if (isLoggedIn && (!Map.isMap(userBillingSubscriptions) || !userBillingSubscriptions.size)) {
    userAccountDataReady = false
  }

  useEffect(() => {
    if (
      planGraphQLActive
        && querySku
        && userAccountDataReady
        && ((!planData && !planDataLoading) || (userLanguage !== goPageUserLanguage))
    ) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus: [exactSku],
        },
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userAccountDataReady, isLoggedIn, userLanguage])

  // on mount
  useEffect(() => {
    updateSeo(props)

    if (!planGraphQLActive) {
      updateData(props)
    }

    if (planGraphQLActive) {
      updateData({
        ...props,
        sku: exactSku,
      })
    }
  }, [])

  // did update
  useEffect(() => {
    const { plans } = props

    if (!planGraphQLActive
      && process.env.BROWSER
      && (Map.isMap(plans) && plans.get('processing') === false)) {
      const newSku = replaceSkuToPriceIncreasedSku(querySku)

      updateData({
        ...props,
        sku: newSku,
      })
    }

    if (
      planGraphQLActive
      && process.env.BROWSER
      && planData && !planDataLoading
    ) {
      updateData({
        ...props,
        sku: exactSku,
        graphQLPlanData: formattedPlanData,
        graphQLPlanDataLoading: planDataLoading,
      })
    }
  })

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setGoPageUserLanguage(userLanguage)
  }, [userLanguage])

  const { staticText } = props

  return (
    <div className="go-page">
      <Sherpa type={TYPE_LARGE} />
      <p className="go-page__message">
        {staticText.getIn(['data', 'oneMomentPleaseWeAreLocatingYourPath'])}
      </p>
    </div>
  )
}

GoPage.propTypes = {
  setPlansLocalizedSelection: PropTypes.func.isRequired,
  inboundTracking: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map,
  page: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  location: PropTypes.object.isRequired,
  setPageSeo: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  doConsumeRemoteLoginCode: PropTypes.func.isRequired,
  setCheckoutPlanPreSelected: PropTypes.func.isRequired,
  setPlansSelection: PropTypes.func.isRequired,
}

export default compose(
  connect(
    (state) => ({
      staticText: state.staticText.getIn(['data', 'goPage']),
      inboundTracking: state.inboundTracking,
      userAccount: state.userAccount,
      ...(!planGraphQLActive && { plans: state.plans }),
      auth: state.auth,
      user: state.user,
      page: state.page,
      remoteLoginCode: state.remoteLoginCode || Map(),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setPlansLocalizedSelection: actions.plans.setPlansLocalizedSelection,
        setPlansSelection: actions.plans.setPlansSelection,
        batchPlaylistItems: actions.playlist.batchPlaylistItems,
        setPageSeo: actions.page.setPageSeo,
        createCustomPlaylistFromDeeplink: actions.playlist.createCustomPlaylistFromDeeplink,
        doConsumeRemoteLoginCode: actions.remoteLoginCode.doConsumeRemoteLoginCode,
        setCheckoutPlanPreSelected: actions.checkout.setCheckoutPlanPreSelected,
      }
    },
  ),
  connectPage({ seoType: PAGE_SEO_TYPE_MANUAL }),
)(GoPage)
